import { useEffect, useMemo, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import { MenuItem, Box, Typography, makeStyles } from '@material-ui/core';
import _ from 'lodash';

import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import FormSection from '../../form/FormSection';
import NgpForm from '../../form/NgpForm';
import NgpSelect from '../../form/NgpSelect';
import NgpTextField from '../../form/NgpTextField';
import { RowFlexLayout } from '../../layout';
import NgpToggle from '../../form/NgpToggle';
import { INTERNAL_ID_FIELD } from '../../form/FormConstants';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';

const useStyles = makeStyles(() => {
    return {
        metricSelect: {
            '& .MuiSelect-select': {
                minWidth: 400,
            },
        },
        metricSelectMenuPaper: {
            maxHeight: 200,
        },
    };
});

const THRESHOLD_METRIC_IDS_FIELD = 'metricIds';
const THRESHOLD_INVERTED_FIELD = 'inverted';
const THRESHOLD_MIN_FIELD = 'min';
const THRESHOLD_DEGRADED_FIELD = 'degraded';
const THRESHOLD_CRITICAL_FIELD = 'critical';
const THRESHOLD_MAX_FIELD = 'max';
const THRESHOLD_NAME_FIELD = 'name';

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const RESERVED_METRICS = [
    'availability',
    'resolved_ip_address',
    'message',
    'status',
    'ispass',
    'statuscounts',
    'wifi_ssid',
];

const defaultValues = {
    [THRESHOLD_METRIC_IDS_FIELD]: [],
    [THRESHOLD_INVERTED_FIELD]: false,
    [THRESHOLD_MIN_FIELD]: 0,
    [THRESHOLD_DEGRADED_FIELD]: 0,
    [THRESHOLD_CRITICAL_FIELD]: 0,
    [THRESHOLD_MAX_FIELD]: 1,
};

function isSelectableMetric(metric) {
    return !RESERVED_METRICS.includes(_.toLower(metric?.name || '')) && !metric?.isString;
}

function ThresholdValueField({ fieldName, label }) {
    return (
        <Box minWidth={100}>
            <NgpTextField fieldName={fieldName} label={label} required fieldType="number" />
        </Box>
    );
}

export default function ThresholdsDialogForm({
    selectedList = EMPTY_ARRAY,
    selectedRecord = EMPTY_OBJECT,
    setSelectedRecord,
    open,
    setOpen,
    onConfirm,
    availableMetrics = EMPTY_ARRAY,
}) {
    const classes = useStyles();
    const methods = useForm({ ...USE_FORM_DEFAULT_OPTIONS, defaultValues, mode: 'onChange' });
    const { handleSubmit, formState, reset, watch, setValue } = methods;

    const internalIdRef = useRef(null);

    const inverted = !!watch(THRESHOLD_INVERTED_FIELD);
    const watchedMin = _.toNumber(watch(THRESHOLD_MIN_FIELD));
    const watchedDegraded = _.toNumber(watch(THRESHOLD_DEGRADED_FIELD));
    const watchedCritical = _.toNumber(watch(THRESHOLD_CRITICAL_FIELD));
    const watchedMax = _.toNumber(watch(THRESHOLD_MAX_FIELD));

    const isOrderValid = inverted
        ? watchedMin <= watchedCritical && watchedCritical <= watchedDegraded && watchedDegraded <= watchedMax
        : watchedMin <= watchedDegraded && watchedDegraded <= watchedCritical && watchedCritical <= watchedMax;

    const selectableMetrics = useMemo(() => {
        const currentMetricId = _.toNumber(_.get(selectedRecord, `${THRESHOLD_METRIC_IDS_FIELD}[0]`, 0));
        const usedMetricIds = _.chain(selectedList)
            .map((threshold) => _.toNumber(_.get(threshold, `${THRESHOLD_METRIC_IDS_FIELD}[0]`, 0)))
            .filter((id) => id > 0 && id !== currentMetricId)
            .value();

        return _.filter(availableMetrics, (metric) => {
            return isSelectableMetric(metric) && !usedMetricIds.includes(_.toNumber(metric?.id));
        });
    }, [availableMetrics, selectedList, selectedRecord]);

    useEffect(() => {
        if (open) {
            const defaultMetricId = _.toNumber(_.get(selectableMetrics, '[0].id', 0));
            const defaultMetricIds = defaultMetricId > 0 ? [defaultMetricId] : [];

            if (!_.isEmpty(selectedRecord)) {
                internalIdRef.current = selectedRecord[INTERNAL_ID_FIELD] || null;

                reset({
                    ...defaultValues,
                    ...selectedRecord,
                    [THRESHOLD_METRIC_IDS_FIELD]: defaultMetricIds,
                    [THRESHOLD_INVERTED_FIELD]: !!selectedRecord?.[THRESHOLD_INVERTED_FIELD],
                    [THRESHOLD_MIN_FIELD]: _.toNumber(selectedRecord?.[THRESHOLD_MIN_FIELD] ?? 0),
                    [THRESHOLD_DEGRADED_FIELD]: _.toNumber(selectedRecord?.[THRESHOLD_DEGRADED_FIELD] ?? 0),
                    [THRESHOLD_CRITICAL_FIELD]: _.toNumber(selectedRecord?.[THRESHOLD_CRITICAL_FIELD] ?? 0),
                    [THRESHOLD_MAX_FIELD]: _.toNumber(selectedRecord?.[THRESHOLD_MAX_FIELD] ?? 1),
                });
            } else {
                internalIdRef.current = null;
                reset({
                    ...defaultValues,
                    [THRESHOLD_METRIC_IDS_FIELD]: defaultMetricIds,
                });
            }
        }
    }, [open, selectedRecord, selectableMetrics, reset]);

    const handleCleanup = () => {
        internalIdRef.current = null;
        setSelectedRecord(undefined);
    };

    const handleToggleInverted = () => {
        setValue(THRESHOLD_MIN_FIELD, 0, { shouldDirty: true, shouldValidate: true });
        setValue(THRESHOLD_DEGRADED_FIELD, 0, { shouldDirty: true, shouldValidate: true });
        setValue(THRESHOLD_CRITICAL_FIELD, 0, { shouldDirty: true, shouldValidate: true });
        setValue(THRESHOLD_MAX_FIELD, 1, { shouldDirty: true, shouldValidate: true });
    };

    const handleDialogSubmit = (dataToSave = {}) => {
        const metricIdValue = _.toNumber(_.get(dataToSave, `${THRESHOLD_METRIC_IDS_FIELD}[0]`, 0));
        const metric = _.find(availableMetrics, (item) => _.toNumber(item?.id) === metricIdValue);

        if (metricIdValue <= 0 || !metric) {
            return;
        }

        const nextData = {
            ...dataToSave,
            [THRESHOLD_METRIC_IDS_FIELD]: [metricIdValue],
            [THRESHOLD_NAME_FIELD]: metric?.name,
            [THRESHOLD_INVERTED_FIELD]: !!dataToSave?.[THRESHOLD_INVERTED_FIELD],
            [THRESHOLD_MIN_FIELD]: _.toNumber(dataToSave?.[THRESHOLD_MIN_FIELD]),
            [THRESHOLD_DEGRADED_FIELD]: _.toNumber(dataToSave?.[THRESHOLD_DEGRADED_FIELD]),
            [THRESHOLD_CRITICAL_FIELD]: _.toNumber(dataToSave?.[THRESHOLD_CRITICAL_FIELD]),
            [THRESHOLD_MAX_FIELD]: _.toNumber(dataToSave?.[THRESHOLD_MAX_FIELD]),
        };

        if (internalIdRef.current) {
            _.set(nextData, INTERNAL_ID_FIELD, internalIdRef.current);
        }

        return Promise.resolve(onConfirm(nextData)).then(handleCleanup);
    };

    const hasSelectableMetrics = selectableMetrics.length > 0;
    const newThreshold = _.isEmpty(selectedRecord);
    const thresholdFieldOrder = inverted
        ? [THRESHOLD_MAX_FIELD, THRESHOLD_DEGRADED_FIELD, THRESHOLD_CRITICAL_FIELD, THRESHOLD_MIN_FIELD]
        : [THRESHOLD_MIN_FIELD, THRESHOLD_DEGRADED_FIELD, THRESHOLD_CRITICAL_FIELD, THRESHOLD_MAX_FIELD];
    const thresholdFieldLabels = {
        [THRESHOLD_MIN_FIELD]: <Trans>Min</Trans>,
        [THRESHOLD_DEGRADED_FIELD]: <Trans>Degraded</Trans>,
        [THRESHOLD_CRITICAL_FIELD]: <Trans>Excessive</Trans>,
        [THRESHOLD_MAX_FIELD]: <Trans>Max</Trans>,
    };

    return (
        <NgpTwoButtonDialog
            title={<Trans>Service Test Threshold</Trans>}
            confirmButtonText={newThreshold ? <Trans>ADD</Trans> : <Trans>UPDATE</Trans>}
            cancelButtonText={<Trans>Cancel</Trans>}
            open={open}
            setOpen={setOpen}
            onCloseFunc={handleCleanup}
            confirmButtonDisabled={!formState.isValid || !hasSelectableMetrics || !isOrderValid}
            callerWillCloseOnConfirm
            onConfirm={handleSubmit(handleDialogSubmit)}
        >
            <Box width={700}>
                <FormProvider {...methods}>
                    <NgpForm
                        initialized
                        loading={false}
                        showSave={false}
                        formPadding={false}
                        showRequiredFieldsTip={false}
                        submitFunction={handleDialogSubmit}
                    >
                        <FormSection>
                            {!hasSelectableMetrics ? (
                                <Typography color="error" variant="body2">
                                    <Trans>No available metrics to threshold.</Trans>
                                </Typography>
                            ) : (
                                newThreshold && (
                                    <Typography variant="body2">
                                        <Trans>Select the metric and set your threshold values.</Trans>
                                    </Typography>
                                )
                            )}
                            <NgpSelect
                                fieldName={`${THRESHOLD_METRIC_IDS_FIELD}.0`}
                                label={<Trans>Metric to Threshold</Trans>}
                                required={newThreshold}
                                disabled={!newThreshold}
                                className={classes.metricSelect}
                                selectProps={{
                                    MenuProps: {
                                        getContentAnchorEl: null,
                                        PaperProps: {
                                            className: classes.metricSelectMenuPaper,
                                        },
                                    },
                                }}
                            >
                                {(selectableMetrics || []).map((metric, index) => {
                                    const metricId = _.toNumber(metric?.id);
                                    const metricLabel = metric?.title || metric?.name || '';
                                    const metricKey = `${metricId > 0 ? metricId : 'metric'}-${metricLabel || index}`;

                                    return (
                                        <MenuItem key={metricKey} value={metric?.id}>
                                            {metricLabel}
                                        </MenuItem>
                                    );
                                })}
                            </NgpSelect>

                            <NgpToggle
                                fieldName={THRESHOLD_INVERTED_FIELD}
                                label={<Trans>Inverted</Trans>}
                                onChangeFunc={handleToggleInverted}
                                helpTipText={t`Inverted indicates that scale for the metric is flipped, i.e. higher values are better`}
                            />

                            {!isOrderValid && (
                                <Typography color="error" variant="body2" style={{ fontWeight: 'bold' }}>
                                    {inverted
                                        ? t`Values must follow Min ≤ Excessive ≤ Degraded ≤ Max`
                                        : t`Values must follow Min ≤ Degraded ≤ Excessive ≤ Max`}
                                </Typography>
                            )}

                            <RowFlexLayout gridGap={6} wrap>
                                {thresholdFieldOrder.map((fieldName) => (
                                    <ThresholdValueField
                                        key={fieldName}
                                        fieldName={fieldName}
                                        label={thresholdFieldLabels[fieldName]}
                                    />
                                ))}
                            </RowFlexLayout>
                        </FormSection>
                    </NgpForm>
                </FormProvider>
            </Box>
        </NgpTwoButtonDialog>
    );
}
